import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Download, Loader2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../ui/table';
import { SessionsTable } from './RolePlayLabReport';

const SectionTable = ({ section, config, renderRow }) => (
    <Card data-testid={`user-report-section-${section.key.replace(':', '-')}`}>
        <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
                {config?.icon && <config.icon className={`h-4 w-4 ${config.color || ''}`} />}
                {config?.label || section.label}
                <Badge variant="outline" className="ml-1 text-xs">{section.count}</Badge>
            </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
            {section.key === 'roleplay_labs' ? <SessionsTable rows={section.rows} /> : (
                <Table>
                    <TableHeader><TableRow>{(config?.columns || []).map(c => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
                    <TableBody>{section.rows.map((row, i) => renderRow(row, i, section.key))}</TableBody>
                </Table>
            )}
        </CardContent>
    </Card>
);

const download = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

// One learner's results across every report, picked from the Reports page search
export const UserReportView = ({ api, user, days, reportConfig, renderRow, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const params = days !== 'all' ? { days } : {};

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData((await api.get(`/reports/users/${user.id}/overview`, { params: days !== 'all' ? { days } : {} })).data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load this user\'s reports');
        } finally {
            setLoading(false);
        }
    }, [api, user.id, days]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = async () => {
        setExporting(true);
        try {
            const res = await api.get(`/reports/users/${user.id}/overview/export`, { params, responseType: 'blob' });
            const name = (user.name || user.email || 'user').toLowerCase().replace(/[^a-z0-9]+/g, '_');
            download(res.data, `user_report_${name}_${new Date().toISOString().split('T')[0]}.csv`);
            toast.success('User report exported');
        } catch {
            toast.error('Failed to export user report');
        } finally {
            setExporting(false);
        }
    };

    const withRows = data?.sections.filter(s => s.count > 0) || [];
    const empty = data?.sections.filter(s => s.count === 0 && !s.error) || [];
    const failed = data?.sections.filter(s => s.error) || [];
    const shown = data?.user || user;

    return (
        <div className="space-y-4" data-testid="user-report-view">
            <Card>
                <CardContent className="flex flex-wrap items-center gap-4 pt-6">
                    <div className="rounded-full bg-primary/10 p-3"><UserRound className="h-6 w-6 text-primary" /></div>
                    <div className="min-w-0 flex-1">
                        <p className="text-lg font-semibold" data-testid="user-report-name">{shown.name || shown.email}</p>
                        <p className="text-sm text-muted-foreground" data-testid="user-report-email">
                            {shown.email}{shown.department ? ` · ${shown.department}` : ''}{shown.designation ? ` · ${shown.designation}` : ''}
                        </p>
                    </div>
                    {data && (
                        <Badge variant="outline" data-testid="user-report-active-count">
                            Activity in {data.active_sections} of {data.sections.length} reports
                        </Badge>
                    )}
                    <Button variant="outline" onClick={onClose} className="gap-2" data-testid="user-report-close-btn">
                        <ArrowLeft className="h-4 w-4" />All reports
                    </Button>
                    <Button onClick={exportCsv} disabled={exporting || loading || !withRows.length} className="gap-2" data-testid="user-report-export-btn">
                        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Export to CSV
                    </Button>
                </CardContent>
            </Card>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : data && (
                <>
                    {withRows.map(s => <SectionTable key={s.key} section={s} config={reportConfig[s.key]} renderRow={renderRow} />)}
                    {failed.length > 0 && (
                        <p className="text-sm text-red-600" data-testid="user-report-errors">
                            Could not load: {failed.map(s => reportConfig[s.key]?.label || s.label).join(', ')}. Please try again.
                        </p>
                    )}
                    {empty.length > 0 && (
                        <p className="text-sm text-muted-foreground" data-testid="user-report-no-activity">
                            No activity{days !== 'all' ? ' in this time range' : ''}: {empty.map(s => reportConfig[s.key]?.label || s.label).join(', ')}
                        </p>
                    )}
                </>
            )}
        </div>
    );
};
